/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Immutable Credit Ledger Summary Cards Component (Task 10.6.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import {
  Wallet,
  ArrowDownLeft,
  Flame,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useCreditLedgerStore } from '../../stores/useCreditLedgerStore';

export const LedgerSummaryCards: React.FC = () => {
  const { t } = useTranslation();
  const { getLedgerStats } = useCreditLedgerStore();

  const stats = getLedgerStats();

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {/* 1. Available Balance */}
      <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between shadow-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-foreground-muted font-medium">
              {t('ledger.currentBalance')}
            </span>
            <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.2 rounded font-mono font-bold bg-emerald-500/10 text-emerald-500">
              <ShieldCheck className="size-2.5" />
              D1
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <p className="text-2xl font-bold font-heading text-foreground">
              {stats.currentBalance.toLocaleString()}
            </p>
            <span className="text-xs font-semibold text-primary">CR</span>
          </div>
        </div>
        <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Wallet className="size-5" />
        </div>
      </div>

      {/* 2. Lifetime Top-ups */}
      <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between shadow-xs">
        <div className="space-y-1">
          <span className="text-xs text-foreground-muted font-medium">
            {t('ledger.totalDeposited')}
          </span>
          <div className="flex items-baseline gap-1.5">
            <p className="text-2xl font-bold font-heading text-emerald-500">
              +{stats.totalDeposited.toLocaleString()}
            </p>
            <span className="text-xs font-medium text-foreground-muted">CR</span>
          </div>
        </div>
        <div className="size-10 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
          <ArrowDownLeft className="size-5" />
        </div>
      </div>

      {/* 3. Lifetime Consumed */}
      <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between shadow-xs">
        <div className="space-y-1">
          <span className="text-xs text-foreground-muted font-medium">
            {t('ledger.totalConsumed')}
          </span>
          <div className="flex items-baseline gap-1.5">
            <p className="text-2xl font-bold font-heading text-rose-500">
              -{stats.totalConsumed.toLocaleString()}
            </p>
            <span className="text-xs font-medium text-foreground-muted">CR</span>
          </div>
        </div>
        <div className="size-10 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center">
          <Flame className="size-5" />
        </div>
      </div>

      {/* 4. Total Refunded */}
      <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between shadow-xs">
        <div className="space-y-1">
          <span className="text-xs text-foreground-muted font-medium">
            {t('ledger.totalRefunded')}
          </span>
          <div className="flex items-baseline gap-1.5">
            <p className="text-2xl font-bold font-heading text-sky-500">
              +{stats.totalRefunded.toLocaleString()}
            </p>
            <span className="text-xs font-medium text-foreground-muted">CR</span>
          </div>
        </div>
        <div className="size-10 rounded-xl bg-sky-500/10 text-sky-500 flex items-center justify-center">
          <RotateCcw className="size-5" />
        </div>
      </div>
    </div>
  );
};
